/**
 * Medicines the prescription form suggests, by generic name, strength and
 * form, in the strengths most often prescribed in India. The list is built
 * into the app, so the server never learns which medicines a doctor looks
 * up. Doctors may still type any name. Brand names are a later update.
 */
export const MEDICINES: readonly string[] = [
  // Fever, pain and inflammation
  'Paracetamol 500 mg tablet',
  'Paracetamol 650 mg tablet',
  'Paracetamol 125 mg/5 ml syrup',
  'Paracetamol 250 mg/5 ml syrup',
  'Ibuprofen 200 mg tablet',
  'Ibuprofen 400 mg tablet',
  'Ibuprofen 100 mg/5 ml syrup',
  'Diclofenac 50 mg tablet',
  'Diclofenac 75 mg/ml injection',
  'Diclofenac 1% gel',
  'Aceclofenac 100 mg tablet',
  'Naproxen 250 mg tablet',
  'Mefenamic acid 250 mg tablet',
  'Mefenamic acid 500 mg tablet',
  'Etoricoxib 90 mg tablet',
  'Tramadol 50 mg capsule',
  'Aspirin 75 mg tablet',
  'Aspirin 150 mg tablet',
  // Antibiotics
  'Amoxicillin 250 mg capsule',
  'Amoxicillin 500 mg capsule',
  'Amoxicillin 125 mg/5 ml syrup',
  'Amoxicillin 500 mg + Clavulanic acid 125 mg tablet',
  'Amoxicillin 875 mg + Clavulanic acid 125 mg tablet',
  'Amoxicillin 200 mg + Clavulanic acid 28.5 mg/5 ml syrup',
  'Azithromycin 250 mg tablet',
  'Azithromycin 500 mg tablet',
  'Azithromycin 200 mg/5 ml syrup',
  'Clarithromycin 500 mg tablet',
  'Cefixime 200 mg tablet',
  'Cefixime 50 mg/5 ml syrup',
  'Cefuroxime 500 mg tablet',
  'Cefpodoxime 200 mg tablet',
  'Cephalexin 500 mg capsule',
  'Ceftriaxone 1 g injection',
  'Ciprofloxacin 500 mg tablet',
  'Levofloxacin 500 mg tablet',
  'Ofloxacin 200 mg tablet',
  'Norfloxacin 400 mg tablet',
  'Doxycycline 100 mg capsule',
  'Metronidazole 400 mg tablet',
  'Metronidazole 200 mg/5 ml syrup',
  'Tinidazole 500 mg tablet',
  'Nitrofurantoin 100 mg capsule',
  'Cotrimoxazole 800 mg + 160 mg tablet',
  'Linezolid 600 mg tablet',
  'Clindamycin 300 mg capsule',
  'Gentamicin 80 mg/2 ml injection',
  // Tuberculosis
  'Isoniazid 300 mg tablet',
  'Rifampicin 450 mg capsule',
  'Pyrazinamide 750 mg tablet',
  'Ethambutol 800 mg tablet',
  // Antifungals, antivirals, antiparasitics
  'Fluconazole 150 mg tablet',
  'Itraconazole 100 mg capsule',
  'Terbinafine 250 mg tablet',
  'Clotrimazole 1% cream',
  'Ketoconazole 2% cream',
  'Luliconazole 1% cream',
  'Acyclovir 400 mg tablet',
  'Acyclovir 800 mg tablet',
  'Valacyclovir 500 mg tablet',
  'Oseltamivir 75 mg capsule',
  'Albendazole 400 mg tablet',
  'Albendazole 200 mg/5 ml suspension',
  'Ivermectin 12 mg tablet',
  'Permethrin 5% cream',
  'Chloroquine 250 mg tablet',
  'Hydroxychloroquine 200 mg tablet',
  'Artemether 80 mg + Lumefantrine 480 mg tablet',
  'Primaquine 7.5 mg tablet',
  // Diabetes
  'Metformin 500 mg tablet',
  'Metformin 500 mg SR tablet',
  'Metformin 850 mg tablet',
  'Metformin 1000 mg SR tablet',
  'Glimepiride 1 mg tablet',
  'Glimepiride 2 mg tablet',
  'Gliclazide 40 mg tablet',
  'Gliclazide 80 mg tablet',
  'Gliclazide 60 mg MR tablet',
  'Glipizide 5 mg tablet',
  'Sitagliptin 50 mg tablet',
  'Sitagliptin 100 mg tablet',
  'Vildagliptin 50 mg tablet',
  'Teneligliptin 20 mg tablet',
  'Dapagliflozin 10 mg tablet',
  'Empagliflozin 10 mg tablet',
  'Empagliflozin 25 mg tablet',
  'Pioglitazone 15 mg tablet',
  'Voglibose 0.2 mg tablet',
  'Insulin regular 40 IU/ml injection',
  'Insulin isophane (NPH) 40 IU/ml injection',
  'Insulin premixed 30/70 40 IU/ml injection',
  'Insulin glargine 100 IU/ml injection',
  // Blood pressure and heart
  'Amlodipine 2.5 mg tablet',
  'Amlodipine 5 mg tablet',
  'Amlodipine 10 mg tablet',
  'Cilnidipine 10 mg tablet',
  'Nifedipine 10 mg tablet',
  'Telmisartan 20 mg tablet',
  'Telmisartan 40 mg tablet',
  'Telmisartan 80 mg tablet',
  'Telmisartan 40 mg + Hydrochlorothiazide 12.5 mg tablet',
  'Telmisartan 40 mg + Amlodipine 5 mg tablet',
  'Losartan 25 mg tablet',
  'Losartan 50 mg tablet',
  'Olmesartan 20 mg tablet',
  'Enalapril 5 mg tablet',
  'Ramipril 2.5 mg capsule',
  'Ramipril 5 mg capsule',
  'Lisinopril 5 mg tablet',
  'Atenolol 25 mg tablet',
  'Atenolol 50 mg tablet',
  'Metoprolol 25 mg tablet',
  'Metoprolol succinate 25 mg ER tablet',
  'Metoprolol succinate 50 mg ER tablet',
  'Bisoprolol 2.5 mg tablet',
  'Bisoprolol 5 mg tablet',
  'Carvedilol 3.125 mg tablet',
  'Carvedilol 6.25 mg tablet',
  'Propranolol 10 mg tablet',
  'Propranolol 40 mg tablet',
  'Hydrochlorothiazide 12.5 mg tablet',
  'Chlorthalidone 12.5 mg tablet',
  'Indapamide 1.5 mg SR tablet',
  'Furosemide 40 mg tablet',
  'Torsemide 10 mg tablet',
  'Spironolactone 25 mg tablet',
  'Prazosin 2.5 mg ER tablet',
  'Isosorbide mononitrate 20 mg tablet',
  'Isosorbide dinitrate 5 mg sublingual tablet',
  'Glyceryl trinitrate 2.6 mg CR tablet',
  'Digoxin 0.25 mg tablet',
  'Amiodarone 200 mg tablet',
  'Ivabradine 5 mg tablet',
  'Clopidogrel 75 mg tablet',
  'Aspirin 75 mg + Clopidogrel 75 mg capsule',
  'Ticagrelor 90 mg tablet',
  'Warfarin 5 mg tablet',
  'Apixaban 5 mg tablet',
  'Rivaroxaban 20 mg tablet',
  // Cholesterol
  'Atorvastatin 10 mg tablet',
  'Atorvastatin 20 mg tablet',
  'Atorvastatin 40 mg tablet',
  'Rosuvastatin 5 mg tablet',
  'Rosuvastatin 10 mg tablet',
  'Rosuvastatin 20 mg tablet',
  'Fenofibrate 160 mg tablet',
  'Ezetimibe 10 mg tablet',
  // Stomach and gut
  'Pantoprazole 40 mg tablet',
  'Omeprazole 20 mg capsule',
  'Rabeprazole 20 mg tablet',
  'Esomeprazole 40 mg tablet',
  'Famotidine 20 mg tablet',
  'Pantoprazole 40 mg + Domperidone 30 mg SR capsule',
  'Domperidone 10 mg tablet',
  'Ondansetron 4 mg tablet',
  'Ondansetron 2 mg/5 ml syrup',
  'Metoclopramide 10 mg tablet',
  'Antacid (aluminium and magnesium hydroxide) suspension',
  'Sucralfate 1 g/10 ml suspension',
  'Oral rehydration salts sachet',
  'Zinc sulphate 20 mg dispersible tablet',
  'Loperamide 2 mg capsule',
  'Racecadotril 100 mg capsule',
  'Lactulose 10 g/15 ml solution',
  'Bisacodyl 5 mg tablet',
  'Ispaghula husk powder',
  'Polyethylene glycol 3350 powder',
  'Dicyclomine 10 mg tablet',
  'Drotaverine 40 mg tablet',
  'Drotaverine 80 mg tablet',
  'Hyoscine butylbromide 10 mg tablet',
  'Ursodeoxycholic acid 300 mg tablet',
  'Rifaximin 550 mg tablet',
  'Mesalamine 400 mg tablet',
  // Breathing and allergy
  'Salbutamol 100 mcg inhaler',
  'Salbutamol 2 mg tablet',
  'Salbutamol 2.5 mg/2.5 ml respules',
  'Budesonide 200 mcg inhaler',
  'Budesonide 0.5 mg/2 ml respules',
  'Formoterol 6 mcg + Budesonide 200 mcg inhaler',
  'Salmeterol 50 mcg + Fluticasone 250 mcg inhaler',
  'Tiotropium 9 mcg inhaler',
  'Ipratropium 20 mcg inhaler',
  'Levosalbutamol 1 mg/5 ml syrup',
  'Montelukast 10 mg tablet',
  'Montelukast 10 mg + Levocetirizine 5 mg tablet',
  'Montelukast 4 mg chewable tablet',
  'Doxofylline 400 mg tablet',
  'Theophylline 300 mg SR tablet',
  'Cetirizine 10 mg tablet',
  'Cetirizine 5 mg/5 ml syrup',
  'Levocetirizine 5 mg tablet',
  'Fexofenadine 120 mg tablet',
  'Fexofenadine 180 mg tablet',
  'Bilastine 20 mg tablet',
  'Chlorpheniramine 4 mg tablet',
  'Hydroxyzine 25 mg tablet',
  'Fluticasone 50 mcg nasal spray',
  'Mometasone 50 mcg nasal spray',
  'Xylometazoline 0.1% nasal drops',
  'Saline 0.65% nasal drops',
  'Dextromethorphan cough syrup',
  'Ambroxol 30 mg/5 ml syrup',
  'Bromhexine 8 mg tablet',
  'Guaifenesin syrup',
  // Steroids and hormones
  'Prednisolone 5 mg tablet',
  'Prednisolone 10 mg tablet',
  'Prednisolone 20 mg tablet',
  'Methylprednisolone 4 mg tablet',
  'Methylprednisolone 8 mg tablet',
  'Dexamethasone 0.5 mg tablet',
  'Dexamethasone 4 mg/ml injection',
  'Hydrocortisone 100 mg injection',
  'Deflazacort 6 mg tablet',
  'Levothyroxine 25 mcg tablet',
  'Levothyroxine 50 mcg tablet',
  'Levothyroxine 75 mcg tablet',
  'Levothyroxine 100 mcg tablet',
  'Carbimazole 5 mg tablet',
  'Methimazole 10 mg tablet',
  'Progesterone 200 mg capsule',
  'Dydrogesterone 10 mg tablet',
  'Norethisterone 5 mg tablet',
  'Ethinylestradiol 0.03 mg + Levonorgestrel 0.15 mg tablet',
  'Clomiphene 50 mg tablet',
  'Letrozole 2.5 mg tablet',
  'Cabergoline 0.5 mg tablet',
  // Vitamins, minerals and pregnancy
  'Folic acid 5 mg tablet',
  'Ferrous sulphate 200 mg + Folic acid 0.5 mg tablet',
  'Ferrous ascorbate 100 mg + Folic acid 1.5 mg tablet',
  'Iron sucrose 100 mg/5 ml injection',
  'Calcium carbonate 500 mg + Vitamin D3 250 IU tablet',
  'Calcium carbonate 1250 mg + Vitamin D3 500 IU tablet',
  'Cholecalciferol 60,000 IU capsule',
  'Methylcobalamin 1500 mcg tablet',
  'Vitamin B complex tablet',
  'Vitamin C 500 mg tablet',
  'Multivitamin and multimineral tablet',
  'Potassium chloride oral solution',
  // Brain, nerves and mind
  'Pregabalin 75 mg capsule',
  'Pregabalin 75 mg + Methylcobalamin 750 mcg capsule',
  'Gabapentin 300 mg capsule',
  'Amitriptyline 10 mg tablet',
  'Amitriptyline 25 mg tablet',
  'Nortriptyline 10 mg tablet',
  'Duloxetine 30 mg capsule',
  'Sertraline 50 mg tablet',
  'Escitalopram 10 mg tablet',
  'Fluoxetine 20 mg capsule',
  'Paroxetine 12.5 mg CR tablet',
  'Mirtazapine 15 mg tablet',
  'Alprazolam 0.25 mg tablet',
  'Alprazolam 0.5 mg tablet',
  'Clonazepam 0.5 mg tablet',
  'Lorazepam 1 mg tablet',
  'Diazepam 5 mg tablet',
  'Zolpidem 5 mg tablet',
  'Olanzapine 5 mg tablet',
  'Risperidone 2 mg tablet',
  'Quetiapine 25 mg tablet',
  'Haloperidol 5 mg tablet',
  'Lithium carbonate 300 mg tablet',
  'Sodium valproate 200 mg tablet',
  'Sodium valproate 500 mg CR tablet',
  'Levetiracetam 500 mg tablet',
  'Carbamazepine 200 mg tablet',
  'Oxcarbazepine 300 mg tablet',
  'Phenytoin 100 mg tablet',
  'Phenobarbitone 30 mg tablet',
  'Lamotrigine 50 mg tablet',
  'Topiramate 25 mg tablet',
  'Flunarizine 10 mg tablet',
  'Betahistine 16 mg tablet',
  'Cinnarizine 25 mg tablet',
  'Prochlorperazine 5 mg tablet',
  'Sumatriptan 50 mg tablet',
  'Donepezil 5 mg tablet',
  'Levodopa 100 mg + Carbidopa 10 mg tablet',
  'Trihexyphenidyl 2 mg tablet',
  // Muscles, bones and joints
  'Thiocolchicoside 4 mg capsule',
  'Methocarbamol 750 mg tablet',
  'Tizanidine 2 mg tablet',
  'Baclofen 10 mg tablet',
  'Colchicine 0.5 mg tablet',
  'Allopurinol 100 mg tablet',
  'Febuxostat 40 mg tablet',
  'Alendronate 70 mg tablet',
  'Methotrexate 7.5 mg tablet',
  'Sulfasalazine 500 mg tablet',
  // Urinary and men's health
  'Tamsulosin 0.4 mg capsule',
  'Silodosin 8 mg capsule',
  'Finasteride 5 mg tablet',
  'Alfuzosin 10 mg ER tablet',
  'Solifenacin 5 mg tablet',
  'Potassium citrate oral solution',
  // Skin
  'Mupirocin 2% ointment',
  'Fusidic acid 2% cream',
  'Silver sulfadiazine 1% cream',
  'Betamethasone valerate 0.1% cream',
  'Clobetasol propionate 0.05% cream',
  'Hydrocortisone 1% cream',
  'Mometasone 0.1% cream',
  'Calamine lotion',
  'Benzoyl peroxide 2.5% gel',
  'Adapalene 0.1% gel',
  'Clindamycin 1% gel',
  'Tretinoin 0.025% cream',
  'Minoxidil 5% solution',
  'Isotretinoin 20 mg capsule',
  // Eyes and ears
  'Moxifloxacin 0.5% eye drops',
  'Ciprofloxacin 0.3% eye drops',
  'Tobramycin 0.3% eye drops',
  'Carboxymethylcellulose 0.5% eye drops',
  'Olopatadine 0.1% eye drops',
  'Timolol 0.5% eye drops',
  'Latanoprost 0.005% eye drops',
  'Prednisolone acetate 1% eye drops',
  'Ciprofloxacin 0.3% ear drops',
  'Clotrimazole 1% ear drops',
  'Wax softening ear drops',
  // Mouth and throat
  'Chlorhexidine 0.2% mouthwash',
  'Povidone iodine 2% gargle',
  'Lignocaine 2% gel',
  'Triamcinolone 0.1% oral paste',
  // Women's health
  'Tranexamic acid 500 mg tablet',
  'Tranexamic acid 500 mg + Mefenamic acid 250 mg tablet',
  'Misoprostol 200 mcg tablet',
  'Clotrimazole 100 mg vaginal tablet',
  // Vaccines and others given in clinic
  'Tetanus toxoid 0.5 ml injection',
  'Normal saline 0.9% 500 ml infusion',
  'Ringer lactate 500 ml infusion',
  'Dextrose 5% 500 ml infusion',
  'Pheniramine 22.75 mg/ml injection',
  'Pantoprazole 40 mg injection',
  'Ondansetron 2 mg/ml injection',
];

/** Words in a medicine's name that tell its form, used to suggest a dose. */
const FORMS: readonly [RegExp, string][] = [
  [/\b(tablet|chewable tablet)\b/i, '1 tablet'],
  [/\bcapsule\b/i, '1 capsule'],
  [/\b(syrup|suspension|solution)\b/i, '5 ml'],
  [/\b(inhaler)\b/i, '2 puffs'],
  [/\brespules\b/i, '1 respule by nebuliser'],
  [/\b(eye|ear|nasal) drops\b/i, '1 drop'],
  [/\bnasal spray\b/i, '1 spray in each nostril'],
  [/\b(cream|gel|ointment|lotion|paste)\b/i, 'Apply a thin layer'],
  [/\bsachet\b/i, '1 sachet'],
  [/\b(injection|infusion)\b/i, 'As directed'],
];

/** A starting dose for a medicine, from its form, for the doctor to change. */
export function suggestedDose(name: string): string {
  return FORMS.find(([form]) => form.test(name))?.[1] ?? '';
}

/** How often to take a medicine, in the way Indian prescriptions write it. */
export const FREQUENCIES = [
  { code: '1-0-0', label: '1-0-0, morning', timesPerDay: 1 },
  { code: '0-1-0', label: '0-1-0, afternoon', timesPerDay: 1 },
  { code: '0-0-1', label: '0-0-1, night', timesPerDay: 1 },
  { code: '1-0-1', label: '1-0-1, morning and night', timesPerDay: 2 },
  { code: '1-1-1', label: '1-1-1, three times a day', timesPerDay: 3 },
  { code: '1-1-1-1', label: '1-1-1-1, four times a day', timesPerDay: 4 },
  { code: 'SOS', label: 'Only when needed (SOS)', timesPerDay: undefined },
  { code: 'STAT', label: 'Once, straight away (STAT)', timesPerDay: undefined },
  { code: 'weekly', label: 'Once a week', timesPerDay: undefined },
] as const;
export type FrequencyCode = (typeof FREQUENCIES)[number]['code'];

/** When to take it in relation to meals. */
export const TIMINGS = ['', 'after food', 'before food', 'with food', 'on an empty stomach', 'at bedtime'] as const;
export type Timing = (typeof TIMINGS)[number];

/** The whole instruction as one line, such as "1 tablet, 1-0-1 (morning and night), after food, for 5 days". */
export function dosageText(input: { dose: string; frequency: FrequencyCode; timing: Timing; days: number | null }): string {
  const frequency = FREQUENCIES.find((f) => f.code === input.frequency);
  const how = frequency ? (frequency.code.includes('-') ? `${frequency.code} (${frequency.label.split(', ')[1]})` : frequency.label.replace(/ \(.*\)$/, '').toLowerCase()) : '';
  const days = input.days ? `for ${input.days} ${input.days === 1 ? 'day' : 'days'}` : '';
  return [input.dose.trim(), how, input.timing, days].filter((part) => part.length > 0).join(', ');
}
